import { useState, useMemo, useEffect, useCallback } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import {
  DataTable,
  Button,
  SelectDropdown,
  SearchInput,
  StatusBadge,
} from "../../../components/ui";
import { getSocieties, getTowersBySociety } from "../../../services/societyService";
import { Download } from "lucide-react";
import { toast } from "react-toastify";

const INITIAL_MOCK_DATA = [
  {
    societyname: "AsmitA House",
    tower: "Main Wing",
    floor: "Ground Floor",
    totalflats: 12,
    occupiedflats: 10,
    availableflats: 2,
  },
  {
    societyname: "AsmitA House",
    tower: "Main Wing",
    floor: "1st Floor",
    totalflats: 16,
    occupiedflats: 14,
    availableflats: 2,
  },
  {
    societyname: "AsmitA House",
    tower: "Main Wing",
    floor: "2nd Floor",
    totalflats: 16,
    occupiedflats: 15,
    availableflats: 1,
  },
  {
    societyname: "AsmitA House",
    tower: "Corporate Annex",
    floor: "Floor 1",
    totalflats: 8,
    occupiedflats: 6,
    availableflats: 2,
  },
  {
    societyname: "Hiranandani Estate",
    tower: "Tower A",
    floor: "Floor 1",
    totalflats: 20,
    occupiedflats: 18,
    availableflats: 2,
  },
  {
    societyname: "Hiranandani Estate",
    tower: "Tower B",
    floor: "Floor 2",
    totalflats: 20,
    occupiedflats: 19,
    availableflats: 1,
  },
];

const FloorSummary = () => {
  const [societies, setSocieties] = useState([]);
  const [selectedSociety, setSelectedSociety] = useState("");
  const [towers, setTowers] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);

  // Fetch real property list
  const fetchSocieties = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSocieties();
      if (Array.isArray(data)) {
        setSocieties(data);
      }
    } catch (err) {
      console.error("Could not fetch societies:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSocieties();
  }, [fetchSocieties]);

  // Fetch towers when selectedSociety changes
  useEffect(() => {
    if (!selectedSociety) {
      setTowers([]);
      setSelectedTower("");
      return;
    }
    const matched = societies.find(
      (s) => s.society_name === selectedSociety || String(s.society_id) === String(selectedSociety)
    );
    if (matched?.society_id) {
      getTowersBySociety(matched.society_id)
        .then((res) => {
          if (Array.isArray(res)) setTowers(res);
        })
        .catch(() => setTowers([]));
    }
  }, [selectedSociety, societies]);

  const propertyOptions = useMemo(() => {
    const list = [{ label: "All Properties", value: "" }];
    societies.forEach((s) => {
      list.push({ label: s.society_name, value: s.society_name });
    });
    return list;
  }, [societies]);

  const towerOptions = useMemo(() => {
    const list = [{ label: "All Towers / Wings", value: "" }];
    towers.forEach((t) => {
      list.push({ label: t.tower_name, value: t.tower_name });
    });
    return list;
  }, [towers]);

  const filteredData = useMemo(() => {
    return INITIAL_MOCK_DATA.filter((u) => {
      if (selectedSociety && u.societyname.toLowerCase() !== selectedSociety.toLowerCase()) return false;
      if (selectedTower && u.tower.toLowerCase() !== selectedTower.toLowerCase()) return false;
      if (search) {
        const q = search.toLowerCase();
        const matchesSociety = u.societyname.toLowerCase().includes(q);
        const matchesTower = u.tower.toLowerCase().includes(q);
        const matchesFloor = u.floor.toLowerCase().includes(q);
        if (!matchesSociety && !matchesTower && !matchesFloor) return false;
      }
      return true;
    });
  }, [selectedSociety, selectedTower, search]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredData.slice(start, start + limit);
  }, [filteredData, page, limit]);

  const handleExport = () => {
    if (!filteredData.length) {
      toast.info("No floor records to export.");
      return;
    }
    const headers = [
      "Sr No",
      "Property",
      "Tower",
      "Floor",
      "Total Units",
      "Occupied Units",
      "Available Units",
    ];
    const rows = filteredData.map((r, i) => [
      i + 1,
      `"${r.societyname}"`,
      `"${r.tower}"`,
      `"${r.floor}"`,
      r.totalflats,
      r.occupiedflats,
      r.availableflats,
    ]);
    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Floor_Summary_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Floor summary exported successfully.");
  };

  const columns = [
    {
      key: "sr_no",
      label: "Sr No",
      render: (_, __, idx) => (page - 1) * limit + idx + 1,
    },
    {
      key: "societyname",
      label: "Property Name",
      sortable: true,
      render: (val) => <strong>{val}</strong>,
    },
    {
      key: "tower",
      label: "Tower / Wing",
      sortable: true,
    },
    {
      key: "floor",
      label: "Floor",
      sortable: true,
    },
    {
      key: "totalflats",
      label: "Total Units",
      sortable: true,
      render: (val) => <span>{val} Units</span>,
    },
    {
      key: "occupiedflats",
      label: "Occupied Units",
      sortable: true,
      render: (val) => (
        <StatusBadge variant="info" label={`${val} Occupied`} showDot={false} />
      ),
    },
    {
      key: "availableflats",
      label: "Available Units",
      sortable: true,
      render: (val) => (
        <StatusBadge variant="success" label={`${val} Available`} showDot={false} />
      ),
    },
  ];

  const paginationConfig = {
    currentPage: page,
    pageSize: limit,
    totalCount: filteredData.length,
    totalPages: Math.ceil(filteredData.length / limit) || 1,
    onPageChange: (newPage) => setPage(newPage),
  };

  return (
    <AdminLayout title="Property Floor Summary">
      <DataTableLayout
        title="Floor & Occupancy Summary"
        filters={
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", width: "100%" }}>
            <div style={{ minWidth: "220px" }}>
              <SelectDropdown
                value={selectedSociety}
                onChange={(e) => {
                  setSelectedSociety(e.target.value);
                  setPage(1);
                }}
                options={propertyOptions}
                placeholder="All Properties"
                isClearable={false}
                isSearchable
              />
            </div>
            {towers.length > 0 && (
              <div style={{ minWidth: "180px" }}>
                <SelectDropdown
                  value={selectedTower}
                  onChange={(e) => {
                    setSelectedTower(e.target.value);
                    setPage(1);
                  }}
                  options={towerOptions}
                  placeholder="All Towers"
                  isClearable={false}
                  isSearchable
                />
              </div>
            )}
            <div style={{ minWidth: "220px", flex: 1 }}>
              <SearchInput
                placeholder="Search property, tower, or floor..."
                value={search}
                onChange={(val) => {
                  setSearch(val);
                  setPage(1);
                }}
              />
            </div>
          </div>
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            icon={<Download size={15} />}
          >
            Export to CSV
          </Button>
        }
      >
        <DataTable
          columns={columns}
          data={paginatedData}
          isLoading={loading}
          pagination={paginationConfig}
          emptyMessage="No floor records found matching your filters."
        />
      </DataTableLayout>
    </AdminLayout>
  );
};

export default FloorSummary;
